import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    BottomSheetSurfaceStyleConstantType,
    BottomSheetCloseStyleConstantType,
    BottomSheetHeaderSpacesType,
    BottomSheetHandleSpacesType,
    BottomSheetFooterSpacesType,
    BottomSheetDynamicStyleType,
    BottomSheetBodySpacesType,
    BottomSheetCloseStyleType
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getElevationStyle,
    SURFACE_LEVELS
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    webStyle
} from "../../utils";

export const BOTTOM_SHEET_PORTAL_NAME = "bottomSheet-system";

export const BOTTOM_SHEET_HANDLE_SPACES: BottomSheetHandleSpacesType = {
    bottom: "spacingXs",
    top: "spacingSm"
};

export const BOTTOM_SHEET_HEADER_SPACES: BottomSheetHeaderSpacesType = {
    topWithoutHandle: "spacingLg",
    closeOffset: "spacingXl",
    horizontal: "spacingLg",
    bottom: "spacingSm",
    top: "spacingSm"
};

export const BOTTOM_SHEET_BODY_SPACES: BottomSheetBodySpacesType = {
    topWithoutChrome: "spacingLg",
    horizontal: "spacingLg",
    bottom: "spacingMd",
    top: "spacingSm"
};

export const BOTTOM_SHEET_FOOTER_SPACES: BottomSheetFooterSpacesType = {
    horizontal: "spacingLg",
    bottom: "spacingLg",
    top: "spacingMd"
};

export const getBottomSheetCloseStyle = ({
    isHandleVisible,
    controlHeights,
    touchTargets,
    pointerType,
    typography,
    iconSizes,
    radiuses,
    borders,
    spaces
}: BottomSheetCloseStyleConstantType): BottomSheetCloseStyleType => {
    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    const headerTop = spaces[isHandleVisible ? BOTTOM_SHEET_HEADER_SPACES.top : BOTTOM_SHEET_HEADER_SPACES.topWithoutHandle];

    const headerEdge = spaces[BOTTOM_SHEET_HEADER_SPACES.horizontal];

    const iconInset = (controlHeights.small - iconSizes.small) / 2;

    return {
        top: headerTop + (typography.titleLargeSize.lineHeight - hitArea.minHeight) / 2,
        end: headerEdge - (hitArea.minWidth - iconSizes.small) / 2,
        borderRadius: concentric({
            inset: headerEdge - iconInset + borders.line,
            outer: radiuses.surface,
            role: radiuses.actions
        })
    };
};

export const getBottomSheetSurfaceStyle = ({
    backgroundColor,
    shapeConfigs,
    shadowAlpha,
    elevations,
    radiuses,
    borders,
    colors
}: BottomSheetSurfaceStyleConstantType): ViewStyle => {
    const surfaceLevel = SURFACE_LEVELS.sheet;

    const {
        borderRadius: surfaceRadius,
        ...surfaceShape
    } = getCornerStyle({
        radius: radiuses.surface,
        shapeConfigs
    });

    return {
        ...getElevationStyle({
            shadowColor: colors.system.shadow,
            level: surfaceLevel.level,
            shadowAlpha,
            elevations
        }),
        borderColor: colors.content.border[surfaceLevel.borderColor],
        backgroundColor: colors.content.container[backgroundColor],
        ...surfaceShape,
        borderTopStartRadius: surfaceRadius,
        borderTopEndRadius: surfaceRadius,
        borderTopWidth: borders.line
    };
};

const stylesheet = StyleSheet.create({
    body: {
        ...webStyle({
            overscrollBehavior: "contain"
        })
    },
    header: {
        borderBottomColor: "transparent",
        borderStyle: "solid",
        flexShrink: 0
    },
    loadingBody: {
        justifyContent: "center",
        alignItems: "center"
    },
    container: {
        flexDirection: "column",
        position: "absolute",
        borderStyle: "solid",
        bottom: 0,
        start: 0,
        end: 0
    },
    draggableContainer: {
        ...webStyle({
            touchAction: "none",
            userSelect: "none"
        })
    },
    draggableHandleContainer: {
        ...webStyle({
            cursor: "grab"
        })
    },
    headerTextContainer: {
        flexDirection: "column"
    },
    draggableBody: {
        ...webStyle({
            touchAction: "none"
        })
    },
    handleContainer: {
        alignItems: "center",
        flexShrink: 0
    },
    footer: {
        borderStyle: "solid",
        flexShrink: 0
    },
    close: {
        position: "absolute"
    },
    title: {
        ...webStyle({
            outlineWidth: 0
        })
    },
    hiddenContent: {
        display: "none"
    }
});

export const useStyles = ({
    handleContainerBackgroundColor,
    handleContainerSpacing,
    handleBackgroundColor,
    isWorkAsFullScreen,
    backgroundColor,
    isCustomPadding,
    isFooterVisible,
    isHandleVisible,
    isHeaderVisible,
    bottomSafeArea,
    controlHeights,
    isCloseVisible,
    handleHeight,
    shapeConfigs,
    shadowAlpha,
    closeStyle,
    elevations,
    isScrolled,
    typography,
    maxHeight,
    radiuses,
    borders,
    colors,
    spaces
}: BottomSheetDynamicStyleType) => {
    const headerTop = spaces[isHandleVisible ? BOTTOM_SHEET_HEADER_SPACES.top : BOTTOM_SHEET_HEADER_SPACES.topWithoutHandle];

    const headerBottom = spaces[BOTTOM_SHEET_HEADER_SPACES.bottom];

    const headerEdge = spaces[BOTTOM_SHEET_HEADER_SPACES.horizontal];

    const bodyTop = spaces[isHeaderVisible || isHandleVisible ? BOTTOM_SHEET_BODY_SPACES.top : BOTTOM_SHEET_BODY_SPACES.topWithoutChrome];

    const bodyBottom = spaces[BOTTOM_SHEET_BODY_SPACES.bottom];

    const styles = {
        handleContainer: {
            paddingBottom: spaces[handleContainerSpacing ?? BOTTOM_SHEET_HANDLE_SPACES.bottom],
            paddingTop: spaces[handleContainerSpacing ?? BOTTOM_SHEET_HANDLE_SPACES.top]
        } as Mutable<ViewStyle>,
        bodyContent: {
            paddingBottom: isFooterVisible ? bodyBottom : bodyBottom + bottomSafeArea,
            paddingHorizontal: spaces[BOTTOM_SHEET_BODY_SPACES.horizontal],
            paddingTop: bodyTop
        } as Mutable<ViewStyle>,
        footer: {
            paddingBottom: spaces[BOTTOM_SHEET_FOOTER_SPACES.bottom] + bottomSafeArea,
            paddingHorizontal: spaces[BOTTOM_SHEET_FOOTER_SPACES.horizontal],
            paddingTop: spaces[BOTTOM_SHEET_FOOTER_SPACES.top],
            borderTopColor: colors.content.border.subtle,
            borderTopWidth: borders.separator
        } as Mutable<ViewStyle>,
        handle: {
            width: spaces.spacingXl + borders.indicator,
            height: handleHeight ?? borders.indicator,
            backgroundColor: colors.content.icon.low,
            borderRadius: radiuses.full
        } as Mutable<ViewStyle>,
        header: {
            borderBottomWidth: borders.separator,
            paddingBottom: headerBottom,
            paddingStart: headerEdge,
            paddingEnd: headerEdge,
            paddingTop: headerTop
        } as Mutable<ViewStyle>,
        container: {
            ...getBottomSheetSurfaceStyle({
                backgroundColor,
                shapeConfigs,
                shadowAlpha,
                elevations,
                radiuses,
                borders,
                colors
            }),
            maxHeight
        } as Mutable<ViewStyle>,
        loadingBody: {
            minHeight: controlHeights.large
        } as Mutable<ViewStyle>,
        headerTextContainer: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        close: {
            top: closeStyle.top,
            end: closeStyle.end
        } as Mutable<ViewStyle>
    };

    if(!isWorkAsFullScreen) {
        styles.container.maxWidth = shapeConfigs.sheetMaxWidth;

        styles.container.marginHorizontal = "auto";
    }

    if(handleContainerBackgroundColor) {
        styles.handleContainer.backgroundColor = colors.content.container[handleContainerBackgroundColor];
    }

    if(handleBackgroundColor) {
        styles.handle.backgroundColor = colors.content.container[handleBackgroundColor];
    }

    if(isScrolled) {
        styles.header.borderBottomColor = colors.content.border.subtle;
    }

    if(isCloseVisible) {
        styles.header.paddingEnd = headerEdge + spaces[BOTTOM_SHEET_HEADER_SPACES.closeOffset];

        styles.header.minHeight = headerTop + typography.titleLargeSize.lineHeight + headerBottom + borders.separator;
    }

    if(!isCustomPadding) {
        return styles;
    }

    delete styles.bodyContent.paddingHorizontal;

    delete styles.bodyContent.paddingBottom;

    delete styles.bodyContent.paddingTop;

    if(!isFooterVisible) {
        styles.bodyContent.paddingBottom = bottomSafeArea;
    }

    return styles;
};
export default stylesheet;
